// src/components/DropInsPage.js
import React, { useCallback, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { useAuth } from "../components/AuthContext";
import { profileAPI } from "../services/api";
import { readBookingReturnFromSearch } from "../utils/bookingReturnUtils";
import { FEATURES } from "../config";
import SEO from "./SEO";
// INTRO-OFFER-001-D — customer-facing intro offer countdown card.
// Mounted as the topmost session-type card per visual spec §3.1. Renders
// nothing when the additive `introOffer` block is absent from /api/profile
// (legacy customer OR feature flag OFF on BE) per spec §3.5 + Scenarios
// 10 + 11. Drop-In page is the "Single Sessions" surface per App.js route
// /drop-in-sessions and the visual-spec §3.1 placement recommendation.
import IntroOfferCard from "./IntroOfferCard";
import "./DropInsPage.css";

const getEffectiveMetadata = (product) => ({
  ...(product.metadata || {}),
  ...(product.price?.metadata || {}),
});

const normalizeValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

const getStudioDayOfWeek = (date = new Date()) => {
  const dayName = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Phoenix",
    weekday: "long",
  }).format(date);

  return [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ].indexOf(dayName);
};

// Day of week (0 = Sunday) of a Phoenix YYYY-MM-DD date. Noon Phoenix time
// (UTC-7, no daylight saving) keeps the weekday stable in any browser zone.
const getStudioDayOfWeekForIsoDate = (isoDate) =>
  getStudioDayOfWeek(new Date(`${isoDate}T12:00:00-07:00`));

const parseValidDayValues = (validDayOfWeek) =>
  String(validDayOfWeek || "")
    .split(",")
    .map((value) => parseInt(value.trim(), 10))
    .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);

const isWellnessWednesdayProduct = (product) => {
  const metadata = getEffectiveMetadata(product);
  const promoKey = normalizeValue(metadata.promoKey);
  const productType = normalizeValue(metadata.productType);
  const productName = normalizeValue(product.name);

  return (
    promoKey === "wellness-wednesday" ||
    productType === "wellness-wednesday" ||
    productName.includes("wellness wednesday")
  );
};

// `dayOfWeek` is today in studio time, or the appointment day when the
// customer comes from the booking purchase-choice flow (a day-limited
// credit such as Wellness Wednesday only works for an appointment on that
// day).
const isProductAvailableOnDay = (product, dayOfWeek) => {
  const metadata = getEffectiveMetadata(product);
  const validDays = parseValidDayValues(metadata.validDayOfWeek);

  if (validDays.length > 0) {
    return validDays.includes(dayOfWeek);
  }

  // Wellness Wednesday defaults to Wednesday-only even if Stripe metadata is incomplete.
  if (isWellnessWednesdayProduct(product)) {
    return dayOfWeek === 3;
  }

  return true;
};

const isUnifiedSharedDropIn = (product) => {
  const metadata = getEffectiveMetadata(product);
  const creditModel = normalizeValue(metadata.creditModel);
  const productType = normalizeValue(metadata.productType);
  const creditCategory = normalizeValue(metadata.creditCategory);
  const productName = normalizeValue(product.name);

  return (
    creditModel === "unified-shared" &&
    (productType === "drop-in" ||
      productType === "dropin" ||
      productType === "single-session" ||
      creditCategory === "drop-in" ||
      creditCategory === "dropin" ||
      isWellnessWednesdayProduct(product) ||
      productName.includes("drop-in") ||
      productName.includes("single session"))
  );
};

const isLegacyDropIn = (product) => {
  const metadata = getEffectiveMetadata(product);
  const productType = normalizeValue(metadata.productType);
  return (
    productType === "drop-in-sauna" ||
    productType === "drop-in-red-light"
  );
};

const DropInsPage = () => {
  const [dropInSessions, setDropInSessions] = useState([]);
  const [message, setMessage] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // INTRO-OFFER-001-D — separate loading flag for the intro-offer card so
  // the skeleton can resolve independently of the products fetch. Avoids
  // the card lingering as a skeleton when only the products call is slow.
  const [introOfferLoading, setIntroOfferLoading] = useState(true);
  const { addItem, cartItems, setBookingReturn } = useShoppingCart();
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ADR-027 §A1.4(a): lift the booking-return context off the URL params
  // (sent here by BookSession's choice surface) into the in-memory cart
  // carrier. CartPage forwards it to the backend as `bookingReturn` so the
  // deployed success_url round-trips it across the external Stripe redirect.
  // Replaces the removed legacy browser-storage stash (the R-020
  // paid-but-stranded vector). Only BE-valid values are stored — an invalid
  // candidate yields null (the BE fail-closed contract).
  useEffect(() => {
    const ctx = readBookingReturnFromSearch(location.search);
    if (ctx) setBookingReturn(ctx);
  }, [location.search, setBookingReturn]);

  // INTRO-OFFER-001-D — re-fetch the profile after a successful claim so the
  // IntroOfferCard observes the BE-flipped state (Scenario 3 cross-fade).
  // Also re-used for the initial mount fetch so we have a single source of
  // truth for /api/profile invalidation.
  const refreshUserProfile = useCallback(async () => {
    if (!token) {
      return;
    }
    try {
      const response = await profileAPI.getProfile();
      setUserProfile(response.data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setIntroOfferLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (token) {
        await refreshUserProfile();
      } else {
        // Anonymous visitor — no profile to fetch, intro offer card cannot
        // render anyway (the additive `introOffer` block is only present on
        // authenticated /api/profile responses).
        setIntroOfferLoading(false);
      }
    };

    const fetchDropInSessions = async () => {
      try {
        console.log("Fetching drop-in sessions");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/products`
        );
        const products = await response.json();
        console.log("All products:", products);

        const unifiedDropIns = products.filter(
          (product) =>
            isUnifiedSharedDropIn(product) || isWellnessWednesdayProduct(product)
        );
        const dropInProducts =
          FEATURES.ENABLE_UNIFIED_STOREFRONT && unifiedDropIns.length > 0
            ? unifiedDropIns
            : products.filter(isLegacyDropIn);

        console.log("Filtered drop-in products:", dropInProducts);

        // Log intro offers specifically
        const introOffers = dropInProducts.filter(
          (product) =>
            product.metadata && product.metadata.isIntroOffer === "true"
        );
        console.log("Intro offers found:", introOffers);

        setDropInSessions(dropInProducts);
      } catch (error) {
        console.error("Error fetching drop-in sessions:", error);
      }
    };

    setLoading(true);
    setIntroOfferLoading(true);
    Promise.all([fetchUserProfile(), fetchDropInSessions()])
      .then(() => {
        console.log("All data loaded successfully");
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading data:", error);
        setLoading(false);
        setIntroOfferLoading(false);
      });
    // refreshUserProfile is intentionally omitted from the dep array — the
    // mount-effect re-runs only on `token` change. Adding the callback to
    // the dep array would re-trigger the products fetch unnecessarily on
    // every refresh-after-claim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Debug user profile
  useEffect(() => {
    if (userProfile) {
      console.log("User profile state updated:", userProfile);
      console.log("hasFreeSauna:", userProfile.hasFreeSauna);
      console.log("hasFreeRedLight:", userProfile.hasFreeRedLight);
      console.log("hasFreeFlexible:", userProfile.hasFreeFlexible);
      console.log("introOffersEligible:", userProfile.introOffersEligible);
    }
  }, [userProfile]);

  // From the booking purchase-choice flow, day-limited products follow the
  // appointment day; otherwise they follow today.
  const bookingReturnContext = readBookingReturnFromSearch(location.search);
  const purchaseDayOfWeek = bookingReturnContext
    ? getStudioDayOfWeekForIsoDate(bookingReturnContext.date)
    : getStudioDayOfWeek();

  // Filter products to show intro offers only to eligible users
  const filteredProducts = dropInSessions.filter((product) => {
    const metadata = getEffectiveMetadata(product);

    // Debug each product being filtered
    console.log("Filtering product:", product.name, metadata);

    if (!isProductAvailableOnDay(product, purchaseDayOfWeek)) {
      console.log(`${product.name} is not available for this day`);
      return false;
    }

    // If it's an intro offer
    if (metadata && metadata.isIntroOffer === "true") {
      console.log(`${product.name} is an intro offer`);

      // Only show if user is logged in and hasn't used the intro offer yet
      if (!userProfile) {
        console.log("No user profile, hiding intro offer");
        return false; // Not logged in
      }

      if (userProfile.introOffersEligible === false) {
        console.log("User is ineligible for intro offers, hiding intro offer");
        return false;
      }

      if (metadata.productType === "drop-in-sauna") {
        const shouldShow =
          userProfile.hasFreeSauna !== true &&
          userProfile.hasFreeRedLight !== true &&
          userProfile.hasFreeFlexible !== true;
        console.log("Intro offer eligibility snapshot:", {
          hasFreeSauna: userProfile.hasFreeSauna,
          hasFreeRedLight: userProfile.hasFreeRedLight,
          hasFreeFlexible: userProfile.hasFreeFlexible,
          introOffersEligible: userProfile.introOffersEligible,
          productType: metadata.productType,
        });
        console.log(
          `Sauna intro offer: hasFreeSauna=${userProfile.hasFreeSauna}, shouldShow=${shouldShow}`
        );
        return shouldShow;
      }

      if (metadata.productType === "drop-in-red-light") {
        const shouldShow =
          userProfile.hasFreeRedLight !== true &&
          userProfile.hasFreeSauna !== true &&
          userProfile.hasFreeFlexible !== true;
        console.log("Intro offer eligibility snapshot:", {
          hasFreeSauna: userProfile.hasFreeSauna,
          hasFreeRedLight: userProfile.hasFreeRedLight,
          hasFreeFlexible: userProfile.hasFreeFlexible,
          introOffersEligible: userProfile.introOffersEligible,
          productType: metadata.productType,
        });
        console.log(
          `Red light intro offer: hasFreeRedLight=${userProfile.hasFreeRedLight}, shouldShow=${shouldShow}`
        );
        return shouldShow;
      }

      const isFlexibleIntroOffer =
        metadata.productType === "drop-in" ||
        metadata.productType === "dropin" ||
        metadata.productType === "single-session" ||
        metadata.creditModel === "unified_shared" ||
        metadata.serviceAccess === "both";

      if (isFlexibleIntroOffer) {
        const shouldShow =
          userProfile.hasFreeFlexible !== true &&
          userProfile.hasFreeSauna !== true &&
          userProfile.hasFreeRedLight !== true;
        console.log("Intro offer eligibility snapshot:", {
          hasFreeSauna: userProfile.hasFreeSauna,
          hasFreeRedLight: userProfile.hasFreeRedLight,
          hasFreeFlexible: userProfile.hasFreeFlexible,
          introOffersEligible: userProfile.introOffersEligible,
          productType: metadata.productType,
          creditModel: metadata.creditModel,
          serviceAccess: metadata.serviceAccess,
        });
        console.log(
          `Flexible intro offer: hasFreeFlexible=${userProfile.hasFreeFlexible}, shouldShow=${shouldShow}`
        );
        return shouldShow;
      }

      console.log("Product doesn't match any specific type, hiding");
      return false;
    }

    // For regular products, always show
    return true;
  });

  const handleAddToCart = (product) => {
    const metadata = getEffectiveMetadata(product);
    const isSoftOpeningPeriod = false;
    const isIntroOffer = metadata && metadata.isIntroOffer === "true";

    // Check if this intro offer is already in the cart
    if (isIntroOffer) {
      const existingIntroOffer = cartItems.find((item) => item.isIntroOffer);

      if (existingIntroOffer) {
        setMessage(
          "You already have an intro offer in your cart. Each customer can only claim one intro offer."
        );
        return; // Prevent adding duplicate intro offer
      }
    }

    // For intro offers, price should be 0
    const price = isIntroOffer
      ? 0
      : isSoftOpeningPeriod
      ? Math.floor(product.price.unit_amount)
      : product.price.unit_amount;

    // For drop-in products, we assume a single session purchase.
    addItem({
      priceId: product.price?.id || product.default_price,
      quantity: 1,
      price: price,
      type: "drop-in",
      name: product.name,
      productType: metadata.productType || "drop-in",
      creditModel: metadata.creditModel || null,
      creditCategory: metadata.creditCategory || "drop_in",
      serviceAccess: metadata.serviceAccess || null,
      promoKey: metadata.promoKey || null,
      validDayOfWeek: metadata.validDayOfWeek || null,
      validSessionTypes: metadata.validSessionTypes || null,
      sessionCredits: 1,
      sessionsPurchased: 1,
      isDiscounted: isSoftOpeningPeriod || isIntroOffer,
      originalPrice: product.price ? product.price.unit_amount : 0,
      isIntroOffer: isIntroOffer,
    });

    setMessage(`${product.name} added to cart!`);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dropins-page">
      <SEO
        title="Drop-In Sessions | Diviti Adora Wellness Studio | Oro Valley AZ"
        description="Book flexible drop-in sessions for infrared sauna and red light therapy at Diviti Adora in Oro Valley. Single sessions available with special intro offers for first-time customers."
        keywords="drop-in sessions, single sessions, infrared sauna session, red light therapy session, wellness session, Oro Valley wellness, intro offer, first-time customer, infrared sauna Oro Valley AZ, red light therapy near Oro Valley, Tucson infrared sauna sessions, LED red light therapy facials Tucson, enhance athletic recovery Oro Valley, improve circulation sauna Oro Valley, boost immune system red light therapy, natural detox methods Oro Valley"
        canonicalUrl="/drop-in-sessions"
      />
      <header className="dropins-header">
        <h1>Drop-In Sessions</h1>
        <p>
          Enjoy flexible drop-in sessions for both sauna and red light therapy.
        </p>
        {/*<div className="soft-opening-promo">
          <h3>Soft Opening Special: 50% OFF! With Promo code SOFT50</h3>
          <p>
            All drop-in sessions are HALF PRICE during our soft opening period
            (April 14 - April 30, 2025)
          </p>
        </div>*/}
      </header>
      {/*
        INTRO-OFFER-001-D — countdown card. Topmost session-type surface per
        visual spec §3.1 — sits above the regular drop-in product cards. The
        component renders null when the additive `introOffer` block is absent
        on /api/profile (legacy customer OR feature flag OFF on BE), so the
        existing layout is unaffected for those cohorts (Scenarios 10, 11).
      */}
      <IntroOfferCard
        introOffer={userProfile?.introOffer}
        loading={introOfferLoading && Boolean(token)}
        onRefreshProfile={refreshUserProfile}
        onBrowseSessions={() => {
          // Scrolling to the session list is the natural next step per
          // visual spec §3.2.2 — the "Browse sessions" CTA on the claimed
          // state guides the customer to the product list directly below.
          const content = document.querySelector(".dropins-content");
          if (content && typeof content.scrollIntoView === "function") {
            content.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }}
        onViewBooking={() => {
          // Routes to the existing customer bookings page. Tech doc Q6 +
          // visual spec §3.2.3 — exact route is the FE's call; /bookings is
          // the established customer-side bookings list.
          navigate("/bookings");
        }}
      />
      <section className="dropins-content">
        {filteredProducts.length === 0 ? (
          <p>No drop-in sessions available at the moment.</p>
        ) : (
          <div className="membership-cards">
            {filteredProducts.map((product) => {
              const metadata = getEffectiveMetadata(product);
              const isIntroOffer = metadata && metadata.isIntroOffer === "true";

              // Check if this intro offer is already in the cart
              const isAlreadyInCart =
                isIntroOffer &&
                cartItems.find(
                  (item) =>
                    item.isIntroOffer &&
                    item.productType === metadata.productType
                );

              return (
                <div
                  key={product.id}
                  className={`membership-card ${
                    isIntroOffer ? "intro-offer-card" : ""
                  }`}
                >
                  {isIntroOffer && (
                    <div className="intro-offer-badge">Intro Offer</div>
                  )}
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                  {metadata && metadata.sku && (
                    <p>SKU: {metadata.sku}</p>
                  )}
                  {product.price && product.price.unit_amount ? (
                    <div className="price-container">
                      {isIntroOffer ? (
                        <p className="intro-price">
                          <strong>FREE for first-time customers!</strong>
                        </p>
                      ) : (
                        <>
                          <p className="original-price">
                            ${(product.price.unit_amount / 100).toFixed(2)}{" "}
                            {product.price.currency.toUpperCase()}
                          </p>
                          {/*<p className="promo-price">
                            <strong>
                              Soft Opening Price: $
                              {(product.price.unit_amount / 200).toFixed(2)}{" "}
                              {product.price.currency.toUpperCase()}
                            </strong>
                          </p>
                          <p className="promo-dates">
                            Valid April 14 - April 30, 2025
                          </p>*/}
                        </>
                      )}
                    </div>
                  ) : (
                    <p>Price not available</p>
                  )}
                  <button
                    className={`add-to-cart-btn ${
                      isAlreadyInCart ? "already-claimed" : ""
                    }`}
                    onClick={() => handleAddToCart(product)}
                    disabled={isAlreadyInCart}
                  >
                    {isAlreadyInCart
                      ? "Already in Cart"
                      : isIntroOffer
                      ? "Claim Intro Offer"
                      : "Add to Cart"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {message && <p className="message">{message}</p>}
      </section>
    </div>
  );
};

export default DropInsPage;
